import React from "react";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { useFrame } from "../frame";
import { usePrefersReducedMotion } from "../reducedMotion";
import { FOOTER_GLOW, HERO_GLOW, PageGlowEngine } from "./pageGlowEngine";

import styles from "./PageGlow.module.scss";

export type PageGlowVariant = "hero" | "footer";

const VARIANTS = {
    hero: { options: HERO_GLOW, threshold: 0, className: styles.hero },
    footer: { options: FOOTER_GLOW, threshold: 0.02, className: styles.footer },
};

interface Props {
    variant: PageGlowVariant;
    /** Placement, bleed and mask. */
    className?: string;
    style?: React.CSSProperties;
    /**
     * The headline's current phrase. Each change counts as one phrase change;
     * every third walks the glow to the next colour family.
     */
    phrase?: number;
    /**
     * The element whose visibility gates the animation. Defaults to the canvas,
     * which is wrong for a fixed canvas: it never leaves the viewport, so pass
     * the in-flow element it is pinned over.
     */
    observeRef?: React.RefObject<Element | null>;
}

export const PageGlow = (props: Props) => {
    const {
        options,
        threshold,
        className: variantClass,
    } = VARIANTS[props.variant];
    const canvasRef = React.useRef<HTMLCanvasElement>(null);
    const engineRef = React.useRef<PageGlowEngine | null>(null);
    const [visible, setVisible] = React.useState(false);
    const reduced = usePrefersReducedMotion();
    const running = visible && !reduced;

    React.useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const engine = new PageGlowEngine(canvas, options);
        engineRef.current = engine;

        // Resizing clears the bitmap, and ResizeObserver runs after this
        // frame's draw, so always redraw or the cleared canvas is painted.
        const relayout = () => {
            engine.resize();
            engine.draw(performance.now(), 0);
        };
        relayout();
        const resizeObserver = new ResizeObserver(relayout);
        resizeObserver.observe(canvas);
        let alive = true;
        void document.fonts.ready.then(() => {
            if (alive) relayout();
        });

        const onPointer = (ev: PointerEvent) =>
            engine.pointer(ev.clientX, ev.clientY);
        window.addEventListener("pointermove", onPointer, { passive: true });

        return () => {
            alive = false;
            resizeObserver.disconnect();
            window.removeEventListener("pointermove", onPointer);
            engineRef.current = null;
        };
    }, [options]);

    React.useEffect(() => {
        const target = props.observeRef?.current ?? canvasRef.current;
        if (!target) return;
        const io = new IntersectionObserver(
            (entries) => entries.forEach((e) => setVisible(e.isIntersecting)),
            { threshold },
        );
        io.observe(target);
        return () => io.disconnect();
    }, [props.observeRef, threshold]);

    // Reduced motion: one still frame.
    React.useEffect(() => {
        if (visible && reduced) engineRef.current?.draw(performance.now(), 0);
    }, [visible, reduced]);

    const lastPhrase = React.useRef(props.phrase);
    React.useEffect(() => {
        if (props.phrase === lastPhrase.current) return;
        lastPhrase.current = props.phrase;
        engineRef.current?.phrase(performance.now());
    }, [props.phrase]);

    useFrame((now, dt) => engineRef.current?.draw(now, dt), running);

    return (
        <canvas
            ref={canvasRef}
            className={concatClassNames([
                styles.glow,
                variantClass,
                props.className,
            ])}
            style={props.style}
            aria-hidden="true"
        />
    );
};
